import { Suspense, type ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { cn } from '@worldcoo/ui/lib/utils';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { DiamondGrid, diamondGlows } from '../primitives';
import { formatBandFigure, formatMillions } from '../figure-format';

/**
 * The two figures, from the same query the hero above this section reads. One component each so the
 * Suspense boundary wraps the number alone — the band's copy needs no data.
 *
 * They replace the hand-off's "+240M€" and "105M", which abbreviated quantities nobody had queried
 * and carried captions describing something else. TEC-3840 settled both with the review.
 *
 * The beneficiaries sum is measured; the points-of-sale figure is a constant the business chose, and
 * the query serves it so this page and www's round-up page cannot disagree about it.
 *
 * The count is printed in full and the beneficiaries sum abbreviated, because that is what each one
 * reads as: four digits is a number, seven digits is a magnitude.
 */
function PointsOfSale() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const { language } = useTranslation();
	return <>{formatBandFigure(language, stats.storesCount)}</>;
}

function Beneficiaries() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const { language } = useTranslation();
	return <>{formatMillions(language, stats.directBeneficiaries)}</>;
}

/** An em dash, not a zero: a figure that has not arrived must not read as a figure of none. */
function FigurePending() {
	return <span className="opacity-40">—</span>;
}

/**
 * One of the tilted figure cards. 198px tall in the hand-off, which is exactly what the 48/32 padding
 * plus the 56px figure and its 14px caption come to — so the height is never set, it is arrived at.
 *
 * `className` carries the surface, the tilt and the position, because all three change per card and
 * only the type scale is shared.
 *
 * `whitespace-nowrap` on the figure is the backstop for a live number outgrowing the card: it should
 * overflow, which somebody reports, rather than wrap, which silently changes this card's height and
 * shoves the two shingled below it. `MAX_BAND_FIGURE_CHARS` is what fails first.
 */
function StatCard(props: { className?: string; value: ReactNode; label: string }) {
	return (
		<div className={cn('text-ink flex flex-col justify-center gap-2 rounded-3xl px-8 py-12', props.className)}>
			<p className="text-5xl leading-[1.3] font-medium tracking-[-0.02em] whitespace-nowrap tabular-nums md:text-[3.5rem]">
				{props.value}
			</p>
			<p className="text-sm leading-[1.5]">{props.label}</p>
		</div>
	);
}

/**
 * "El poder de las microdonaciones" — the third About section.
 *
 * A 738px linen band with the copy on the left and three cards shingled down the right, each tilted a
 * different way (2°, -3°, 6.32°) and each overlapping the one above by ~20px. Their coordinates are
 * CENTRE-relative in the hand-off (`calc(50% - w/2 + 330px)`), so they are reproduced that way rather
 * than as offsets from the left edge — otherwise they walk out of the frame between 1280 and 1440.
 *
 * Below `xl` the cluster comes back into the flow and stacks, keeping the tilts and the overlap.
 */
export function Microdonations() {
	const { t } = useLingui();
	return (
		<section className="bg-linen relative overflow-hidden">
			{/* The texture is a single glow in the bottom-left corner, measured off the hand-off: a 777x501
			    ellipse centred 20px below the band's bottom edge, 106px in from the left. Anchored to the
			    BOTTOM so it stays put when the band grows past 738px on a narrow screen. Ink at ~0.28 peak,
			    which is what the design's own dots sample to. */}
			<DiamondGrid
				color="var(--color-ink)"
				opacity={0.7}
				masks={[diamondGlows({ x: '106px', y: 'calc(100% - 20px)' })]}
			/>

			<div className="relative mx-auto flex max-w-[1440px] flex-col gap-12 px-6 py-16 md:py-20 xl:h-[738px] xl:flex-row xl:items-center xl:gap-0 xl:px-0 xl:py-0">
				{/* 13px between heading and body, and 653/591/611 widths: all off the export. */}
				<div className="flex w-full max-w-[653px] flex-col gap-[13px] xl:ml-[109px]">
					<h2 className="text-ink text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`The power of microdonations`}
					</h2>
					{/* Figma holds both paragraphs in ONE text node separated by an empty line, so the gap between
					    them is one line of the 150% leading — 24px, not a paragraph spacing of its own. */}
					<div className="text-ink flex max-w-[611px] flex-col gap-6 text-base leading-[1.5] tracking-[-0.02em]">
						<p>
							<Trans>
								What began as an idea in <strong className="font-semibold">2012</strong> has become a real force for
								change. Our commitment is to keep moving forward, continuously backing innovation, transparency and
								social commitment, our core values.
							</Trans>
						</p>
						<p>
							{/* One `<strong>` per country, not one around the list: the bold is meant to pick the
							    countries out, and wrapping the whole phrase emboldens the commas and the conjunction
							    with them. Three tags also let a translator reorder the list, which one cannot. */}
							<Trans>
								We currently operate directly in <strong className="font-semibold">Spain</strong>,{' '}
								<strong className="font-semibold">Italy</strong> and <strong className="font-semibold">Andorra</strong>,
								expanding our impact day by day.
							</Trans>
						</p>
					</div>
				</div>

				{/* Out of the flow only from `xl`, where the three cards take the hand-off's coordinates. */}
				<div className="flex flex-col items-center xl:absolute xl:inset-0 xl:block">
					<StatCard
						className="bg-sand w-full max-w-[342px] rotate-2 xl:absolute xl:top-[97px] xl:left-1/2 xl:ml-[159px] xl:w-[342px]"
						value={
							<Suspense fallback={<FigurePending />}>
								<PointsOfSale />
							</Suspense>
						}
						label={t`Active points of sale.`}
					/>
					<StatCard
						className="bg-apricot -mt-5 w-full max-w-[342px] -rotate-3 xl:absolute xl:top-[272px] xl:left-1/2 xl:mt-0 xl:ml-[236px] xl:w-[342px]"
						value={
							<Suspense fallback={<FigurePending />}>
								<Beneficiaries />
							</Suspense>
						}
						label={t`Beneficiaries of our projects.`}
					/>

					{/* The map card holds no copy — the export gives it the same 48/32 padding as its siblings,
					    but the artwork is absolutely positioned and bleeds past it, so only the frame is kept.
					    A plain <img>, like the hero's illustration: imgproxy has nothing to do to an SVG.

					    The artwork is COUNTER-ROTATED by the card's own 6.32°. The map sits at 16.84° in the
					    hand-off and the SVG was exported with that already baked in (its clip path carries the
					    rotation), so letting it ride the card's transform would land it at 23.16° — a tilt you
					    can read off the coastline. Undoing the card's angle puts it back at the 16.84° drawn.
					    The corners the counter-rotation exposes show the card's olive, which is the same colour
					    as the map's own sea, so no scale-up is needed to hide them. */}
					<div className="bg-olive relative isolate -mt-5 aspect-[300/210] w-full max-w-[300px] rotate-[6.32deg] overflow-hidden rounded-3xl xl:absolute xl:top-[451px] xl:left-1/2 xl:mt-0 xl:ml-[162px] xl:w-[300px]">
						<img
							src="/redesign/about/europe-map.svg"
							/* Decorative: the paragraph beside it already names the three countries the pins mark. */
							alt=""
							aria-hidden
							className="absolute inset-0 size-full -rotate-[6.32deg] object-cover"
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
